"use client";

import {
  Button,
  ContentLayout,
  Pagination,
  ProductCard,
  SkeletonProductCard,
  Tile,
} from "@2u-landing-pages/components";
import { Bento } from "@2u-landing-pages/templates";
import {
  TypeBentoWithoutUnresolvableLinksResponse,
  TypeContent_fullWithoutUnresolvableLinksResponse,
  TypeProduct_cardWithoutUnresolvableLinksResponse,
  TypeTileWithoutUnresolvableLinksResponse,
} from "@2u-landing-pages/types";
import { camelCase, useIntersectionObserver } from "@2u-landing-pages/utils";
import EventClient from "@2u-landing-pages/utils/event-client";
import { documentToPlainTextString } from "@contentful/rich-text-plain-text-renderer";
import { Block } from "@contentful/rich-text-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getProgramList } from "./filterAndSort";
import { CustomCSSProperties, GalleryProps } from "./Gallery.types";
//@ts-ignore
import styles from "./Gallery.module.scss";

interface NetworkInformation {
  effectiveType: string;
}

interface NavigatorWithConnection extends Navigator {
  connection: NetworkInformation;
}

const SORT_OPTIONS = {
  "Distance - Low to High": "proximity:asc",
  "Distance - High to Low": "proximity:desc",
  "Tuition Cost - Low to High": "tuition_cost:asc",
  "Tuition Cost - High to Low": "tuition_cost:desc",
  "Start Date - Closest to Latest": "start_date:asc",
  "Start Date - Latest to Closest": "start_date:desc",
} as const;

type SortOptionValue = (typeof SORT_OPTIONS)[keyof typeof SORT_OPTIONS];

/* Resize Product Cards to the same height
  ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */
const useEqualHeights = () => {
  const observerRef = useRef<ResizeObserver | null>(null);
  const elementsRef = useRef<Map<any, HTMLElement>>(new Map());

  const RECOMMENDED_FLAG_HEIGHT = 32;
  const BANNER_HEIGHT = 48;

  const hasClassWithPartialName = (el: HTMLElement, partialName: string) => {
    return Array.from(el.classList).some((className) => className.includes(partialName));
  };

  const updateHeights = useCallback(() => {
    requestAnimationFrame(() => {
      // Get only visible elements
      const visibleElements = Array.from(elementsRef.current.values()).filter((el) => el.offsetParent !== null);
      if (visibleElements.length === 0) return;

      // Reset all heights first - this could be done only once before grouping
      visibleElements.forEach((el) => {
        el.style.minHeight = "0";
      });

      const rowGroups = new Map<number, HTMLElement[]>();

      // First pass: group elements by their top position
      for (const el of visibleElements) {
        const topPosition = Math.round(el.getBoundingClientRect().top);
        const TOLERANCE = 50;

        let closestRow = null;
        let minDiff = TOLERANCE + 1;

        Array.from(rowGroups.entries()).forEach(([existingTop, elements]) => {
          const diff = Math.abs(existingTop - topPosition);
          if (diff <= TOLERANCE && diff < minDiff) {
            closestRow = existingTop;
            minDiff = diff;
          }
        });

        if (closestRow !== null) {
          rowGroups.get(closestRow)!.push(el);
        } else {
          rowGroups.set(topPosition, [el]);
        }
      }

      // Second pass: set the heights for each row group
      Array.from(rowGroups.values()).forEach((elementsInRow, index) => {
        // Skip single-column rows
        if (elementsInRow.length === 1) return;

        const hasBanner = elementsInRow.some((el: HTMLElement) => hasClassWithPartialName(el, "cardWithBanner"));
        const hasRecommended = elementsInRow.some((el: HTMLElement) => hasClassWithPartialName(el, "recommended"));
        const hasBannerAndRecommended = elementsInRow.some(
          (el: HTMLElement) =>
            hasClassWithPartialName(el, "cardWithBanner") && hasClassWithPartialName(el, "recommended")
        );

        // Calculate max height in a single pass
        let maxRowHeight = 0;
        for (const el of elementsInRow) {
          // Find all the required sections
          const accordionRef = (el as any).accordionRef;
          const featuredSection = el.querySelector('[class*="ProductCard_featuredSection"]');
          const content = el.querySelector('[class*="ProductCard_content"]');
          const bannerBottom = el.querySelector('[class*="ProductCard_bannerBottom"]');
          const ctaSection = el.querySelector('[class*="ProductCard_ctaSection"]');

          // Calculate the sum of the heights of required sections
          let totalSectionsHeight = 0;
          const accordionHeight = accordionRef?.current?.clientHeight || 0;
          if (featuredSection) totalSectionsHeight += featuredSection.getBoundingClientRect().height;
          if (content) totalSectionsHeight += content.getBoundingClientRect().height;
          if (bannerBottom) totalSectionsHeight += bannerBottom.getBoundingClientRect().height;
          if (ctaSection) totalSectionsHeight += ctaSection.getBoundingClientRect().height;

          maxRowHeight = Math.max(maxRowHeight, totalSectionsHeight - accordionHeight);
        }

        for (const el of elementsInRow) {
          const hasBannerOnCard = hasClassWithPartialName(el, "cardWithBanner");
          const isRecommendedOnCard = hasClassWithPartialName(el, "recommended");

          let marginTop = 0;

          // Case 1: Row has at least one card with both banner and recommended
          if (hasBannerAndRecommended) {
            const FLAG_HEIGHT = index === 0 ? 0 : RECOMMENDED_FLAG_HEIGHT;
            if (hasBannerOnCard && isRecommendedOnCard) {
              marginTop = FLAG_HEIGHT;
            } else {
              marginTop = hasBannerOnCard ? FLAG_HEIGHT : FLAG_HEIGHT + BANNER_HEIGHT;
            }
          }
          // Case 2: Row has at least one banner (but no cards with both banner and recommended)
          else if (hasBanner) {
            marginTop = hasBannerOnCard ? 0 : BANNER_HEIGHT;
          }
          // Case 3: Row has at least one recommended flag but no banners
          else if (hasRecommended && index >= 1) {
            marginTop = isRecommendedOnCard ? RECOMMENDED_FLAG_HEIGHT : 0;
          }

          el.style.marginTop = `${marginTop}px`;
          el.style.minHeight = `${maxRowHeight + (hasBannerOnCard ? BANNER_HEIGHT : 0)}px`;
        }
      });
    });
  }, []);

  useEffect(() => {
    observerRef.current = new ResizeObserver(updateHeights);
    return () => observerRef.current?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const registerElement = useCallback((key: string | number, element: HTMLElement | null) => {
    if (element) {
      elementsRef.current.set(key, element);
      observerRef.current?.observe(element);
    } else {
      const existingElement = elementsRef.current.get(key);
      if (existingElement) {
        observerRef.current?.unobserve(existingElement);
        elementsRef.current.delete(key);
      }
    }
  }, []);

  return { registerElement, updateHeights };
};

export const Gallery: React.FC<GalleryProps> = ({
  grid,
  show_filters,
  section_heading,
  section_background_color,
  gallery_card_size,
  sidebar,
  inspector,
  itemsPerPage = 9,
  lob,
}) => {
  const [filters, setFilters] = useState({});
  const [programList, setProgramList] = useState<TypeProduct_cardWithoutUnresolvableLinksResponse[]>([]);
  const [programPage, setProgramPage] = useState<TypeProduct_cardWithoutUnresolvableLinksResponse[]>([]);
  const [customStyles, setCustomStyles] = useState<CustomCSSProperties>({});
  const customOrderReceived = useRef(false);
  const consentOverride = useRef(false);
  const [customProgramOrder, setCustomProgramOrder] = useState<string[]>([]);
  const [locationConsent, setLocationConsent] = useState(false);
  const [sort, setSort] = useState<SortOptionValue>(SORT_OPTIONS["Distance - Low to High"]);
  const sortSelectRef = useRef<HTMLSelectElement>(null);
  const schoolSelectRef = useRef<HTMLSelectElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const { registerElement, updateHeights } = useEqualHeights();

  const productCards = useMemo(() => grid?.fields?.product_cards || [], [grid?.fields?.product_cards]);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const consentCheck = params.get("consentCheck");
      consentOverride.current = consentCheck?.toLowerCase() === "false";
    }
  }, []);

  const handleIntersect = useCallback((target: HTMLElement) => {
    const { position, name } = target.dataset;

    EventClient.elementViewed({
      webElement: {
        elementType: "Landing Page",
        htmlId: window.location.href,
        location: position,
        text: name,
      },
    });
  }, []);

  const addObserver = useIntersectionObserver(handleIntersect, { threshold: 0.5 });

  useEffect(() => {
    if (gallery_card_size !== "Large grid") updateHeights();
  }, [gallery_card_size, currentPage, updateHeights]);

  useEffect(() => {
    if (typeof window !== "undefined" && window.cohesion) {
      window.cohesion("monarch:done", function (err: object | null, result: any) {
        // Prevents monarch:done from being called multiple times
        if (customOrderReceived.current) return;
        const monarchDecisionResults = result?.source?.trafficFlow?.value?.items?.[0]?.fields?.programs || [];
        customOrderReceived.current = true;
        setCustomProgramOrder(monarchDecisionResults.map((program: any) => program?.sys?.id).filter(Boolean));
      });

      window.cohesion("consent:ready", function () {
        const chsn: any = window?.cohesion;
        const groups = chsn?.consent("list");
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));

        if (consentCaptured) {
          setLocationConsent(true);
        }
      });

      window.cohesion("consent:changed", function (groups) {
        const consentCaptured = Boolean(groups?.find((group: string) => group === "C0002"));
        if (consentCaptured) {
          setLocationConsent(true);
        }
      });
    }
  }, []);

  // Set isLoading to false after 1 second if consent hasn't been captured
  useEffect(() => {
    const isMounted = { current: true };

    const connection =
      "connection" in navigator ? (navigator as NavigatorWithConnection).connection.effectiveType : "unknown";
    const connectionTimeout = connection === "4g" ? 8000 : 12000;

    const timeoutId = setTimeout(() => {
      if (isMounted.current) setIsLoading(false);
    }, connectionTimeout);

    return () => {
      isMounted.current = false;
      clearTimeout(timeoutId);
    };
  }, []);

  function onFilterChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setFilters({ ...filters, school: e.target.value });
  }

  function onSortChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setSort(e.target.value as SortOptionValue);
  }

  useEffect(() => {
    const filteredAndSortedPrograms = getProgramList(
      productCards as TypeProduct_cardWithoutUnresolvableLinksResponse[],
      filters,
      sort,
      customProgramOrder,
      consentOverride.current || locationConsent
    );

    const pinnedPrograms = filteredAndSortedPrograms.filter((program) => Boolean(program.fields.pin_to_bottom));
    const unpinnedPrograms = filteredAndSortedPrograms.filter((program) => !Boolean(program.fields.pin_to_bottom));

    setProgramList([...unpinnedPrograms, ...pinnedPrograms]);
  }, [productCards, filters, sort, customProgramOrder, locationConsent, consentOverride]);

  useEffect(() => {
    const firstCardIsRecommended = programList[0]?.fields?.product_card_flag;

    const customStyleObj: CustomCSSProperties = {};
    if (section_background_color) customStyleObj["--background-color"] = `var(${section_background_color})`;

    if (firstCardIsRecommended && currentPage === 1 && lob === "degree")
      customStyleObj["--recommended-flag-height"] = "32px";
    if (lob === "execed") customStyleObj["--recommended-flag-padding"] = "48px";

    setCustomStyles(customStyleObj);
  }, [programList, currentPage, section_background_color, lob]);

  useEffect(() => {
    const paginatedPrograms = programList?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    setProgramPage(paginatedPrograms);

    updateHeights();

    if (consentOverride.current || locationConsent || customProgramOrder.length > 0) {
      setTimeout(() => setIsLoading(false), 250);
    }
  }, [programList, currentPage, itemsPerPage, updateHeights, consentOverride, locationConsent, customProgramOrder]);

  const handleResetFilters = () => {
    setFilters({});
    setSort(SORT_OPTIONS["Distance - Low to High"]);

    // Reset the select elements using refs
    if (sortSelectRef.current) sortSelectRef.current.value = "";
    if (schoolSelectRef.current) schoolSelectRef.current.value = "";
  };

  // Extract unique schools from programs
  const schoolOptions = (() => {
    const schoolsMap = new Map();

    productCards?.forEach((program) => {
      const school = program?.fields?.program_meta?.fields?.school?.fields;
      if (school && school.name && school.abbreviation) {
        schoolsMap.set(school.abbreviation, {
          name: school.name,
          abbreviation: school.abbreviation,
        });
      }
    });

    return Array.from(schoolsMap.values());
  })();

  return (
    <div className={`gallery-container ${styles.galleryContainer}`} style={customStyles}>
      <div className={styles.galleryWrapper}>
        {section_heading && (
          <div className={styles.galleryHeader}>
            <ContentLayout
              key={section_heading?.sys?.id}
              content={section_heading as TypeContent_fullWithoutUnresolvableLinksResponse}
            />
          </div>
        )}
        {/* Filters and Sort
       ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */}
        {show_filters && (
          <div className={styles.galleryFilters}>
            <div className={styles.galleryFiltersSelects}>
              <label htmlFor="sortFilter">Sort</label>
              <select id="sortFilter" ref={sortSelectRef} onChange={onSortChange} value={sort}>
                <option value="">--- Select Sort ---</option>
                {Object.entries(SORT_OPTIONS).map(([label, value]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <label htmlFor="schoolFilter">School</label>
              <select id="schoolFilter" ref={schoolSelectRef} onChange={onFilterChange}>
                <option value="">--- Select School Filter ---</option>
                {schoolOptions.map((school) => (
                  <option key={school.abbreviation} value={school.abbreviation}>
                    {school.name}
                  </option>
                ))}
              </select>
              <div className={styles.galleryFiltersActions}>
                <Button className={styles.galleryFiltersReset} onClick={handleResetFilters} variation="plain">
                  Reset filters
                </Button>
              </div>
            </div>
            <div className={styles.galleryFilterTotal}>
              <span>
                {programList?.length} Result{programList?.length === 1 ? "" : "s"}
              </span>
            </div>
          </div>
        )}

        <main className={`${styles.gallery} ${(sidebar || []).length > 0 ? styles.hasSidebar : styles.noSidebar}`}>
          {/* Product Card Gallery
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */}
          <div
            ref={gridRef}
            className={`${styles.galleryProducts} ${gallery_card_size ? styles[camelCase(gallery_card_size)] : ""}`}
          >
            {isLoading &&
              Array.from({ length: itemsPerPage }).map((_, i) => (
                <SkeletonProductCard key={`skeleton-${i}`} show={isLoading} />
              ))}

            {!isLoading &&
              programPage.map((program, index) => {
                if (!program || !program.fields || !program.sys?.id) return null;
                const programId = program.sys.id;
                const school_name = program?.fields?.heading || "";
                const program_name = program?.fields?.eyebrow || "";

                return (
                  <ProductCard
                    key={`${programId}-${index}`}
                    ref={(el) => {
                      if (el) {
                        el.dataset.location = "Gallery";
                        el.dataset.position = index.toString();
                        el.dataset.name = `${school_name} - ${program_name}`;
                        addObserver(el);
                        registerElement(`${programId}-${index}`, el);
                      }
                    }}
                    index={index}
                    product={program}
                  />
                );
              })}
          </div>

          {/* Sidebar
        ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ */}
          {(sidebar || []).length > 0 && (
            <div className={styles.gallerySidebar}>
              {(sidebar || []).map((item, index) => {
                if (!item) return null;
                const contentType = item?.sys?.contentType?.sys?.id;
                const id = item?.sys?.id;
                if (!id) return null;

                const uniqueKey = `${id}_${index}`;

                switch (contentType) {
                  case "content_full":
                    const { fields: { heading: contentHeading, subhead: contentSubhead } = {} } =
                      item as TypeContent_fullWithoutUnresolvableLinksResponse;
                    const contentHeadingText = contentHeading
                      ? documentToPlainTextString(contentHeading as unknown as Block)
                      : "";
                    const contentSubheadText = contentSubhead
                      ? documentToPlainTextString(contentSubhead as unknown as Block)
                      : "";
                    return (
                      <div
                        key={uniqueKey}
                        ref={(element) => {
                          if (element) addObserver(element);
                        }}
                        data-location="Sidebar"
                        data-position="Sidebar"
                        data-name={contentHeadingText || contentSubheadText}
                      >
                        <ContentLayout content={item as TypeContent_fullWithoutUnresolvableLinksResponse} />
                      </div>
                    );
                  case "tile":
                    const { fields: { heading: tileHeading, subhead: tileSubhead } = {} } =
                      item as TypeTileWithoutUnresolvableLinksResponse;
                    const tileHeadingText = tileHeading
                      ? documentToPlainTextString(tileHeading as unknown as Block)
                      : "";
                    const tileSubheadText = tileSubhead
                      ? documentToPlainTextString(tileSubhead as unknown as Block)
                      : "";
                    return (
                      <div
                        key={uniqueKey}
                        ref={(element) => {
                          if (element) addObserver(element);
                        }}
                        data-location="Sidebar"
                        data-position="Sidebar"
                        data-name={tileHeadingText || tileSubheadText}
                      >
                        <Tile
                          className={styles.tile}
                          content={item as TypeTileWithoutUnresolvableLinksResponse}
                          padded
                          heading={{ variant: "heading-lg" }}
                          body={{ variant: "body-sm" }}
                          mobileOrientation="vertical"
                          desktopOrientation="vertical"
                        />
                      </div>
                    );
                  case "bento":
                    const { fields: { section_heading } = {} } = item as TypeBentoWithoutUnresolvableLinksResponse;
                    const bentoHeadingText = section_heading
                      ? documentToPlainTextString(section_heading?.fields?.heading as unknown as Block)
                      : "";
                    return (
                      <div
                        key={uniqueKey}
                        ref={(element) => {
                          if (element) addObserver(element);
                        }}
                        data-location="Sidebar"
                        data-position="Sidebar"
                        data-name={bentoHeadingText}
                      >
                        <Bento
                          id={id}
                          {...((item as TypeBentoWithoutUnresolvableLinksResponse).fields || {})}
                          inspector={inspector}
                        />
                      </div>
                    );
                  default:
                    return null;
                }
              })}
            </div>
          )}
        </main>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(programList?.length / itemsPerPage)}
        onPageChange={(page) => {
          setCurrentPage(page);
          const galleryContainer = document.querySelector(".gallery-container");
          if (galleryContainer) {
            galleryContainer.scrollIntoView({ behavior: "smooth" });
          }
        }}
      />
    </div>
  );
};
